import { Icon } from '../ui/Icon';
import { Badge, Button, Card } from '../ui/primitives';
import type { AuthStore } from '../../types/auth';
import type { WasteTeamToday } from '../../types/waste';
import { resolveStoreTimezone } from '../../utils/timezones';
import { WASTE_CATEGORY_LABELS } from '../../utils/wasteCategories';
import { formatWasteQuantity, formatWasteTime, sortWasteForReview, wasteFlagBadges, wasteMeasureText } from '../../utils/wasteReview';
import { CardHead, InlineError } from './InicioParts';

const TITLE_ID = 'inicio-waste-title';
const PREVIEW_ROWS = 3;

interface InicioWasteProps {
  data: WasteTeamToday | null;
  error: boolean;
  onRetry: () => void;
  stores: AuthStore[];
  onNavigate: (destination: string) => void;
}

/**
 * «Mermas de hoy» en el Inicio de dueño y gerente (F1, D-MERMA-F1-BANDEJA-EN-INICIO). No está en la
 * maqueta aprobada, pero es una bandeja real que ya vivía aquí: se conserva con sus mismos datos y su
 * mismo enlace, con la piel nueva. La bandeja completa y la anulación siguen en la sección Mermas.
 *
 * Sin mermas vigentes no hay nada que revisar y la tarjeta no aparece, como antes. Si la consulta
 * falla, ahora se dice (antes la franja simplemente no salía).
 */
export function InicioWaste({ data, error, onRetry, stores, onNavigate }: InicioWasteProps) {
  if (error) {
    return (
      <Card aria-labelledby={TITLE_ID} as="section">
        <CardHead id={TITLE_ID} title="Mermas de hoy" />
        <InlineError onRetry={onRetry} text="No pude traer las mermas de hoy." />
      </Card>
    );
  }
  if (!data || data.summary.activeCount === 0) return null;

  const rows = sortWasteForReview(data.items.filter((item) => !item.reversed)).slice(0, PREVIEW_ROWS);
  const money = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(data.summary.netValue));
  const multiStore = stores.length > 1 || new Set(data.items.map((item) => item.storeId)).size > 1;
  // F2: lo que de verdad falta revisar (sin «Ya la revisé»); con un backend anterior, las marcadas.
  const toReview = data.summary.pendingReviewCount ?? data.summary.flaggedCount;
  const notes = [
    toReview > 0 ? `${toReview} marcada(s) para revisar` : null,
    // El total en pesos no puede incluir lo que no tiene costo: se dice cuántas son, como en Mermas.
    data.summary.uncostedCount > 0 ? `${data.summary.uncostedCount} sin costo registrado (no suman al total)` : null,
  ].filter(Boolean).join(' · ');

  return (
    <Card aria-labelledby={TITLE_ID} as="section">
      <CardHead id={TITLE_ID} title="Mermas de hoy" />
      <p className="n3-inicio__waste-total">{data.summary.activeCount} merma(s) · {money}{data.summary.partial ? ' (parcial)' : ''}</p>
      {notes && <p className="n3-inicio__support">{notes}.</p>}
      <ul className="n3-inicio__waste-rows">
        {rows.map((item) => (
          <li className="n3-inicio__waste-row" key={item.id}>
            <p className="n3-inicio__item-title">{formatWasteQuantity(item.quantity, item.unitSymbol)} de {item.ingredientName}</p>
            <p className="n3-inicio__item-detail">
              {formatWasteTime(item.occurredAt, resolveStoreTimezone(stores, { storeId: item.storeId }))}
              {' · '}{item.category ? WASTE_CATEGORY_LABELS[item.category] ?? item.category : item.legacyReason ?? 'Sin categoría'}
              {' · '}{item.createdBy?.name ?? 'Sin usuario'}
              {multiStore ? ` · ${item.storeName}` : ''}
              {' · '}{wasteMeasureText(item)}
            </p>
            {item.flags.length > 0 && (
              <p className="n3-inicio__waste-flags">
                {wasteFlagBadges(item.flags).map((badge) => (
                  <Badge key={badge.flag} tone={badge.variant === 'danger' ? 'danger' : 'warning'}>{badge.label}</Badge>
                ))}
              </p>
            )}
          </li>
        ))}
      </ul>
      <Button className="n3-inicio__link n3-inicio__more" iconEnd={<Icon name="chevron-right" size={16} />} onClick={() => onNavigate('waste')} variant="ghost">Revisar las mermas de hoy</Button>
    </Card>
  );
}
